/**
 * Emit hosts for the Heidi II explore-later nudges.
 *
 * They stay app-side because every one of them names a `TrackingEventName`
 * member, which the `@heidi/agents` `/ui` zone refuses: the package surfaces
 * take each as a port from `src/utils/heidi-2-ports-wiring.ts`. The payloads
 * are the ones the surfaces emitted before the move, byte for byte.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';

/** One spotlight of the Heidi II Evidence tour came into view. */
export const trackHeidi2EvidenceTourStepViewed = ({
  step,
  totalSteps,
}: {
  step: number;
  totalSteps: number;
}): void => {
  track({
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: ScreenName.Heidi2EvidenceTourSpotlight,
      step_number: step,
      total_steps: totalSteps,
    },
    options: { amplitude: false },
  });
};

/** The Heidi 1 early-access awareness banner was shown. */
export const trackHeidi2AwarenessBannerViewed = (): void => {
  track({
    event_name: TrackingEventName.ScreenViewed,
    props: { screen_name: ScreenName.Heidi2AwarenessBanner },
    options: { amplitude: false, snowplow: true },
  });
};

/** The Heidi 1 non-admin request-access banner was shown. */
export const trackHeidi2RequestAccessBannerViewed = (): void => {
  track({
    event_name: TrackingEventName.ScreenViewed,
    props: { screen_name: ScreenName.Heidi2RequestAccessBanner },
    options: { amplitude: false },
  });
};

/** The explore-later top banner was shown. */
export const trackHeidi2ExploreLaterBannerViewed = (): void => {
  track({
    event_name: TrackingEventName.ScreenViewed,
    props: { screen_name: ScreenName.Heidi2ExploreLaterBanner },
    options: { amplitude: false, snowplow: true },
  });
};

/** The Heidi II "Get started" checklist was shown. */
export const trackHeidi2GetStartedChecklistViewed = (): void => {
  track({
    event_name: TrackingEventName.ScreenViewed,
    props: { screen_name: ScreenName.Heidi2GetStartedChecklist },
    options: { amplitude: false, snowplow: true },
  });
};

/** The Work-home Heidi II onboarding banner was shown. */
export const trackHeidi2NewUserHomeOnboardingBannerViewed = (): void => {
  track({
    event_name: TrackingEventName.ScreenViewed,
    props: { screen_name: ScreenName.Heidi2HomeChangeboardingBanner },
    options: { amplitude: false, snowplow: true },
  });
};
